import styled from '@emotion/styled';
import { useRef, useState, useEffect } from 'react';
import { RubikBold } from 'shared/components/Text';
import * as Colors from 'shared/styles/colors';
import * as ColorSets from 'shared/types/colorSets';
import CategoryPicker from './components/CategoryPicker';
import { Category, Breakpoint, ACCESSORY_CATEGORY_GROUP, BACKGROUND_CATEGORY_GROUP } from 'shared/types';
import ColorPicker from './components/ColorPicker';
import AvatarLayers from './components/AvatarLayers';
import OptionPicker from './components/OptionPicker';
import DownloadIcon from 'shared/components/DownloadIcon';
import { ReactComponent as FandomLogo } from 'shared/images/Fandom_avatar_builder_lockup_2-1.svg';
import * as LayoutUtils from './utils/LayoutUtils';
import { media } from "@mverissimoo/emotion-grid";
import Modal from 'react-modal';
import { css } from '@emotion/react';
import { ROUTES } from 'shared/constants';
import { useHistory } from 'react-router-dom';
import domtoimage from "dom-to-image-more";
import { saveAs } from 'file-saver';
import { ColorState, OptionState } from './types';
import SubcategoryPicker from './components/SubcategoryPicker';

interface OptionColorContainerProps {
  canSelectColor: boolean;
  subcategoriesVisible: boolean;
}

interface ColorPickerContainerProps {
  isBottomView: boolean;
}

Modal.setAppElement('#react-account-root');

const HomePage = () => {
  const avatarRef = useRef(null);
  const history = useHistory();

  const [selectedCategory, setSelectedCategory] = useState<Category>(Category.HEAD);
  const [showSubcategoryPicker, setShowSubcategoryPicker] = useState<boolean>(false);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [optionState, setOptionState] = useState<OptionState>({
    head: 1,
    hair: 1,
    eyes: 1,
    nose: 1,
    mouth: 1,
    ears: 1,
    facialHair: 0,
    accessories: {
      eyewear: 0,
      headgear: 0,
      fanfetti: 0,
    },
    logo: 1,
    achievements: 1,
    background: 2,
  });

  const [colorState, setColorState] = useState<ColorState>({
    skin: ColorSets.SkinColors[0],
    hair: ColorSets.HairBaseColors[1],
    mouth: ColorSets.MouthColors[0],
    accessories: {
      eyewear: ColorSets.AccessoryBaseColors[0],
      headgear: ColorSets.AccessoryBaseColors[0],
      fanfetti: ColorSets.AccessoryBaseColors[0],
    },
    background: ColorSets.BackgroundBaseColors[3],
  });

  useEffect(() => {
    if (
      ACCESSORY_CATEGORY_GROUP.includes(selectedCategory) ||
      BACKGROUND_CATEGORY_GROUP.includes(selectedCategory)
    ) {
      setShowSubcategoryPicker(true);
    } else {
      setShowSubcategoryPicker(false);
    }
  }, [selectedCategory]);

  const updateColor = (color: string) => {
    switch (selectedCategory) {
      case Category.HEAD: {
        setColorState((prevState) => ({ ...prevState, skin: color }));
        break;
      }
      case Category.HAIR: {
        setColorState((prevState) => ({ ...prevState, hair: color }));
        break;
      }
      case Category.MOUTH: {
        setColorState((prevState) => ({ ...prevState, mouth: color }));
        break;
      }
      case Category.EARS: {
        setColorState((prevState) => ({ ...prevState, skin: color }));
        break;
      }
      case Category.FACIAL_HAIR: {
        setColorState((prevState) => ({ ...prevState, hair: color }));
        break;
      }
      case Category.ACCESSORIES_EYEWEAR: {
        setColorState((prevState) => ({ ...prevState, accessories: { ...prevState.accessories, eyewear: color } }));
        break;
      }
      case Category.ACCESSORIES_HEADGEAR: {
        setColorState((prevState) => ({ ...prevState, accessories: { ...prevState.accessories, headgear: color } }));
        break;
      }
      case Category.ACCESSORIES_FANFETTI: {
        setColorState((prevState) => ({ ...prevState, accessories: { ...prevState.accessories, fanfetti: color } }));
        break;
      }
      case Category.BACKGROUND: {
        setColorState((prevState) => ({ ...prevState, background: color }));
        break;
      }
      default:
      // Cannot change colors for other categories
    }
  };

  const updateOption = (option: number) => {
    switch (selectedCategory) {
      case Category.HAIR: {
        setOptionState((prevState) => ({ ...prevState, hair: option }));
        break;
      }
      case Category.EYES: {
        setOptionState((prevState) => ({ ...prevState, eyes: option }));
        break;
      }
      case Category.NOSE: {
        setOptionState((prevState) => ({ ...prevState, nose: option }));
        break;
      }
      case Category.MOUTH: {
        setOptionState((prevState) => ({ ...prevState, mouth: option }));
        break;
      }
      case Category.EARS: {
        setOptionState((prevState) => ({ ...prevState, ears: option }));
        break;
      }
      case Category.FACIAL_HAIR: {
        setOptionState((prevState) => ({ ...prevState, facialHair: option }));
        break;
      }
      case Category.ACCESSORIES_EYEWEAR: {
        setOptionState((prevState) => ({ ...prevState, accessories: { ...prevState.accessories, eyewear: option } }));
        break;
      }
      case Category.ACCESSORIES_HEADGEAR: {
        setOptionState((prevState) => ({ ...prevState, accessories: { ...prevState.accessories, headgear: option } }));
        break;
      }
      case Category.ACCESSORIES_FANFETTI: {
        setOptionState((prevState) => ({ ...prevState, accessories: { ...prevState.accessories, fanfetti: option } }));
        break;
      }
      case Category.LOGO: {
        setOptionState((prevState) => ({ ...prevState, logo: option }));
        break;
      }
      case Category.BACKGROUND: {
        setOptionState((prevState) => ({ ...prevState, background: option }));
        break;
      }
      default:
      // Cannot change colors for other categories
    }
  };

  const goToLandingPage = () => {
    history.push(ROUTES.LANDING);
  };

  const handleExport = () => {
    setTimeout(() => {
      domtoimage.toBlob(avatarRef.current).then((blob) => {
        saveAs(blob, "avatar.png");
      });
    }, 500);
  };

  const handleExportButtonClick = () => {
    setIsModalOpen(true);
  }

  const handleExportConfirm = () => {
    handleExport();
    setIsModalOpen(false);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  return (
    <HomePageContainer>
      <OuterContainer>
        <HeaderContainer>
          <LogoWrapper onClick={() => goToLandingPage()}>
            <FandomLogo />
          </LogoWrapper>
          <ExportButton onClick={() => handleExportButtonClick()}>
            <DownloadIconWrapper>
              <DownloadIcon color={Colors.cream} />
            </DownloadIconWrapper>
            <ExportButtonText>
              EXPORT!
            </ExportButtonText>
          </ExportButton>
        </HeaderContainer>
        <MainContentContainer>
          <AvatarViewContainer>
            <AvatarView ref={avatarRef}>
              <AvatarLayers
                optionState={optionState}
                colorState={colorState}
              />
            </AvatarView>
          </AvatarViewContainer>
          <MainUIContainer>
            <CategorySelectContainer>
              <CategoryPicker
                selectedCategory={selectedCategory}
                setSelectedCategory={setSelectedCategory}
              />
            </CategorySelectContainer>
            {showSubcategoryPicker &&
              <SubcategorySelectContainer>
                <SubcategoryPicker
                  selectedCategory={selectedCategory}
                  setSelectedCategory={setSelectedCategory}
                />
              </SubcategorySelectContainer>
            }
            {/* Color picker becomes main window for head (skin) */}
            <OptionColorContainer
              canSelectColor={LayoutUtils.canSelectColor(selectedCategory, optionState) && selectedCategory !== Category.HEAD}
              subcategoriesVisible={showSubcategoryPicker}
            >
              {selectedCategory !== Category.HEAD &&
                <OptionPickerContainer>
                  <OptionPicker
                    category={selectedCategory}
                    optionState={optionState}
                    updateOption={updateOption}
                  />
                </OptionPickerContainer>
              }
              {LayoutUtils.canSelectColor(selectedCategory, optionState) &&
                <ColorPickerContainer isBottomView={selectedCategory !== Category.HEAD}>
                  <ColorPicker
                    category={selectedCategory}
                    optionState={optionState}
                    colorState={colorState}
                    updateColor={updateColor}
                    isMainView={selectedCategory === Category.HEAD}
                  />
                </ColorPickerContainer>
              }
            </OptionColorContainer>
          </MainUIContainer>
        </MainContentContainer>
      </OuterContainer>
      {/* Export Confirm Modal */}
      <Modal
        isOpen={isModalOpen}
        onRequestClose={() => closeModal()}
        style={modalStyle}
        contentLabel="Confirm Export Avatar"
      >
        <ModalContentContainer>
          <ModalSubtitleText>
            Are you ready to export your avatar?
          </ModalSubtitleText>
          <ModalButtonRow>
            <ModalConfirmButton onClick={() => handleExportConfirm()}>
              <ModalText>
                YES
              </ModalText>
            </ModalConfirmButton>
            <ModalCancelButton onClick={() => closeModal()}>
              <ModalText>
                NO
              </ModalText>
            </ModalCancelButton>
          </ModalButtonRow>
        </ModalContentContainer>
      </Modal>
    </HomePageContainer>
  );
}

export default HomePage;

const HomePageContainer = styled.div({
  display: 'flex',
  width: '100vw',
  height: 'auto',
  position: 'relative',
  backgroundImage: 'url("/site_background_3840x2160_1-1.png")',
  justifyContent: 'center',
  alignContent: 'center',
  alignItems: 'center',
  paddingTop: 24,
  paddingBottom: 36,
  margin: 0,
  [media(Breakpoint.lg)]: {
    height: '100vh',
    padding: 0,
  },
});

const OuterContainer = styled.div({
  width: 340,
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  borderWidth: LayoutUtils.STANDARD_BORDER_WIDTH,
  borderStyle: 'solid',
  borderColor: Colors.purple,
  borderRadius: 4,
  boxShadow: '12px 12px 0px 0px rgba(80, 16, 67, 1)',
  alignSelf: 'center',
  margin: 0,
  [media(Breakpoint.lg)]: {
    width: 'auto',
    boxShadow: '16px 16px 0px 0px rgba(80, 16, 67, 1)',
  },
});

const HeaderContainer = styled.div({
  display: 'flex',
  width: '100%',
  height: 64,
  borderBottomWidth: 4,
  borderBottomStyle: 'solid',
  borderBottomColor: Colors.purple,
  justifyContent: 'space-between',
  backgroundColor: Colors.purple,
  [media(Breakpoint.lg)]: {
    height: LayoutUtils.STANDARD_HEADER_HEIGHT,
  },
});

const LogoWrapper = styled.button({
  width: 240,
  cursor: 'pointer',
  borderWidth: 0,
  alignSelf: 'center',
  backgroundColor: Colors.purple,
  paddingLeft: 18,
  [media(Breakpoint.lg)]: {
    width: 300,
  },
});

const ExportButton = styled.button({
  display: 'flex',
  cursor: 'pointer',
  width: 'auto',
  flexDirection: 'row',
  borderWidth: 0,
  justifyContent: 'center',
  alignContent: 'center',
  alignItems: 'center',
  backgroundColor: Colors.red,
  padding: 12,
  [media(Breakpoint.lg)]: {
    width: 225,
  },
});

const DownloadIconWrapper = styled.span({
  height: LayoutUtils.STANDARD_SUPPORT_UI_SQUARE_SIDE,
  width: LayoutUtils.STANDARD_SUPPORT_UI_SQUARE_SIDE,
  marginLeft: 4,
  marginRight: 4,
  [media(Breakpoint.lg)]: {
    marginLeft: 0,
    marginRight: 20,
  },
});

const ExportButtonText = styled.span([
  RubikBold,
  {
    color: Colors.cream,
    alignItems: 'center',
    fontSize: 20,
    display: 'none',
    [media(Breakpoint.lg)]: {
      display: 'flex',
    },
  }
]);

const MainContentContainer = styled.div({
  display: 'flex',
  flexWrap: 'wrap',
  width: '100%',
  position: 'relative',
  backgroundColor: Colors.white,
});

const AvatarViewContainer = styled.div({
  display: 'flex',
  width: 300,
  height: 300,
  justifyContent: 'center',
  alignItems: 'center',
  alignSelf: 'center',
  borderBottomWidth: 4,
  borderBottomStyle: 'solid',
  borderBottomColor: Colors.purple,
  backgroundColor: Colors.white,
  padding: 30,
  [media(Breakpoint.lg)]: {
    width: LayoutUtils.STANDARD_MAIN_WINDOW_HEIGHT,
    height: LayoutUtils.STANDARD_MAIN_WINDOW_HEIGHT,
    borderBottomWidth: 0,
    padding: 0,
  },
});

const AvatarView = styled.div({
  position: 'relative',
  width: 280,
  height: 280,
  overflow: 'hidden',
  backgroundColor: Colors.transparent,
  [media(Breakpoint.lg)]: {
    width: LayoutUtils.STANDARD_AVATAR_VIEW_SIDE,
    height: LayoutUtils.STANDARD_AVATAR_VIEW_SIDE,
  },
})

const MainUIContainer = styled.div({
  width: '100%',
  height: 300,
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
  borderLeftWidth: 0,
  borderLeftStyle: 'solid',
  borderLeftColor: Colors.purple,
  [media(Breakpoint.lg)]: {
    borderLeftWidth: 6,
    width: LayoutUtils.STANDARD_UI_WINDOW_WIDTH,
    height: LayoutUtils.STANDARD_MAIN_WINDOW_HEIGHT,
  },
});

const CategorySelectContainer = styled.div({
  display: 'flex',
  position: 'relative',
  width: '100%',
  height: 64,
  borderBottomWidth: LayoutUtils.STANDARD_BORDER_WIDTH,
  borderBottomStyle: 'solid',
  borderColor: Colors.purple,
  justifyContent: 'flex-start',
  [media(Breakpoint.lg)]: {
    width: '100%',
    height: 'auto',
  },
});

const SubcategorySelectContainer = styled.div({
  display: 'flex',
  position: 'relative',
  width: '100%',
  height: 32,
  borderBottomWidth: LayoutUtils.STANDARD_BORDER_WIDTH,
  borderBottomStyle: 'solid',
  borderColor: Colors.purple,
  justifyContent: 'flex-start',
  [media(Breakpoint.lg)]: {
    width: '100%',
    height: 'auto',
  },
});

const OptionColorContainer = styled.div<OptionColorContainerProps>(({ canSelectColor, subcategoriesVisible }) => ([
  {
    overflowY: 'auto',
    backgroundColor: Colors.white,
  },
  subcategoriesVisible ? {
    height: canSelectColor ? 140 : 202,
    [media(Breakpoint.lg)]: {
      height: canSelectColor ? 288 : 375,
    },
  } : {
    height: canSelectColor ? 172 : 234,
    [media(Breakpoint.lg)]: {
      height: canSelectColor ? 330 : 417,
    },
  }
]));

const ColorPickerContainer = styled.div<ColorPickerContainerProps>(({ isBottomView }) => ([
  isBottomView ? {
    position: 'absolute',
    bottom: 0,
    width: '100%',
    display: 'flex',
    borderTopWidth: LayoutUtils.STANDARD_BORDER_WIDTH,
    borderTopStyle: 'solid',
    borderTopColor: Colors.purple,
    boxShadow: BOTTOM_COLOR_PICKER_SHADOW,
    justifyContent: 'flex-start',
    zIndex: 200,
  } : {
    position: 'relative',
    width: '100%',
    display: 'flex',
    justifyContent: 'flex-start',
  }]
));

const OptionPickerContainer = styled.div({
  position: 'relative',
  display: 'flex',
  justifyContent: 'flex-start',
});

const modalStyle = {
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999,
  },
  content: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: LayoutUtils.MODAL_WIDTH,
    height: LayoutUtils.MODAL_HEIGHT,
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderWidth: LayoutUtils.STANDARD_BORDER_WIDTH,
    borderColor: Colors.purple,
    borderStyle: 'solid',
    padding: 0,
  },
};

const ModalContentContainer = styled.div({
  backgroundColor: Colors.white,
});

const modalTextStyle = css([
  RubikBold,
  {
    width: '100%',
    textAlign: 'center',
    color: Colors.purple,
  },
]);

const ModalSubtitleText = styled.div([
  modalTextStyle,
  {
    fontSize: 19,
  },
]);

const ModalText = styled.div([
  modalTextStyle,
  {
    fontSize: 16,
  },
]);

const ModalButtonRow = styled.div({
  width: '100%',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  marginTop: 20,
});

const modalButtonStyle = css({
  display: 'flex',
  flexDirection: 'row',
  width: LayoutUtils.MODAL_BUTTON_WIDTH,
  height: LayoutUtils.MODAL_BUTTON_HEIGHT,
  borderWidth: LayoutUtils.STANDARD_BORDER_WIDTH,
  borderColor: Colors.purple,
  borderStyle: 'solid',
  justifyContent: 'center',
  alignContent: 'center',
  alignItems: 'center',
});

const ModalConfirmButton = styled.button([
  modalButtonStyle,
  {
    backgroundColor: Colors.lightBlue,
    marginRight: 24,
  },
]);

const ModalCancelButton = styled.button([
  modalButtonStyle,
  {
    backgroundColor: Colors.yellow,
  },
]);

const BOTTOM_COLOR_PICKER_SHADOW = `
  0px -0.7px 0.6px rgba(0, 0, 0, 0.028),
  0px -1.7px 1.3px rgba(0, 0, 0, 0.04),
  0px -3.1px 2.5px rgba(0, 0, 0, 0.05),
  0px -5.6px 4.5px rgba(0, 0, 0, 0.06),
  0px -10.4px 8.4px rgba(0, 0, 0, 0.072),
  0px -25px 20px rgba(0, 0, 0, 0.1)
`;
